import type { DefaultValue } from 'recoil';
import { selector, waitForAll, waitForNone } from 'recoil';
import type { BenchmarkConfig, Subject } from '../types';
import type { AnalysisSubject, ComputedDateRange } from 'venn-utils';
import { MAX_SUBJECT_SIZE, assertNotNil, hasDisabledBenchmark } from 'venn-utils';
import {
  analysisSubjects,
  blockAnalysisSubjects,
  blockFees,
  blockSubjects,
  globalSubjectFees,
  globalSubjects,
  overriddenBlockSubjects,
  unlinkedSubjectFees,
} from './subjects';
import {
  blockBenchmarkConfig,
  globalBenchmarkConfig,
  globalBenchmarkRelative,
  globalBenchmarkSubject,
  globalBenchmarkType,
  unlinkedBenchmarkRelative,
  unlinkedBenchmarkSubject,
  unlinkedBenchmarkType,
} from './benchmark';
import { blockDateRange, globalDateRange, overriddenBlockDateRange } from './dateRange';
import { blockInfoGraphicType, blockMaxSubjects, customizedBlock } from './blockConfig';
import type { CustomizedBlock } from 'venn-api';
import { isLinked } from './linked';
import { secondarySubject } from './allocator';
import { compact } from 'lodash';
import { selectedBlockIdState } from '../sessionState';
import { blockSettings } from './blockSettings';

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const rightPanelSubjects = selector<Subject[]>({
  key: 'rightPanelSubjects',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return selected ? get(blockSubjects(selected)) : get(globalSubjects);
  },
  set: ({ get, set }, subjects: Subject[] | DefaultValue) => {
    const selected = get(selectedBlockIdState);
    if (selected) {
      set(blockSubjects(selected), subjects);
    } else {
      set(globalSubjects, subjects);
    }
  },
});

/**
 * Only for IMv1. Use `subjectInputGroupFees` for IMv2.
 */
export const rightPanelFees = selector<Record<string, number>>({
  key: 'rightPanelFees',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return selected ? get(blockFees(selected)) : get(globalSubjectFees);
  },
  set: ({ set, get }, newValue) => {
    const selected = get(selectedBlockIdState);
    selected ? set(blockFees(selected), newValue) : set(globalSubjectFees, newValue);
  },
});

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const rightPanelAnalysisSubjects = selector<AnalysisSubject[]>({
  key: 'rightPanelAnalysisSubjects',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    if (selected) {
      return get(blockAnalysisSubjects(selected));
    }
    const subjects = get(globalSubjects);
    return get(analysisSubjects(subjects));
  },
});

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const rightPanelDateRange = selector<ComputedDateRange>({
  key: 'rightPanelDateRange',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return selected
      ? assertNotNil(get(blockDateRange(selected)), 'block date range must not be nil in input management V1')
      : get(globalDateRange);
  },
  set: ({ get, set }, dateRange: ComputedDateRange | DefaultValue) => {
    const selected = get(selectedBlockIdState);
    if (selected) {
      set(blockDateRange(selected), dateRange);
    } else {
      set(globalDateRange, dateRange);
    }
  },
});

export const rightPanelBenchmarkConfig = selector<BenchmarkConfig>({
  key: 'rightPanelBenchmarkConfig',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return selected ? get(blockBenchmarkConfig(selected)) : get(globalBenchmarkConfig);
  },
  set: ({ get, set }, benchmarkConfig: BenchmarkConfig | DefaultValue) => {
    const selected = get(selectedBlockIdState);
    if (selected) {
      set(blockBenchmarkConfig(selected), benchmarkConfig);
    } else {
      set(globalBenchmarkConfig, benchmarkConfig);
    }
  },
});

export const rightPanelMaxSubjects = selector<number>({
  key: 'rightPanelMaxSubjects',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return selected ? get(blockMaxSubjects(selected)) : MAX_SUBJECT_SIZE;
  },
});

export const rightPanelExcludedSubjects = selector<number>({
  key: 'rightPanelExcludedSubjects',
  get: ({ get }) => {
    const maxSubjects = get(rightPanelMaxSubjects);
    const subjects = get(rightPanelSubjects);
    const secondarySubjects = get(waitForAll(subjects.map((subject) => secondarySubject(subject))));
    return subjects.length + compact(secondarySubjects).length - maxSubjects;
  },
});

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const rightPanelInputsEditable = selector<boolean>({
  key: 'rightPanelInputsEditable',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return !selected || !get(isLinked(selected));
  },
});

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const rightPanelIsLinked = selector<boolean>({
  key: 'rightPanelIsLinked',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return !!selected && get(isLinked(selected));
  },
  set: ({ get, set }, linked) => {
    const selected = get(selectedBlockIdState);

    if (!selected) {
      return;
    }

    set(isLinked(selected), linked);

    // If we unlink, set the overridden input states to the current global states
    if (!linked) {
      const infoGraphicType = get(blockInfoGraphicType(selected));
      const blockSetting = get(blockSettings(selected));
      const isBenchmarkDisabled = hasDisabledBenchmark(blockSetting.customBlockType, infoGraphicType);
      if (!isBenchmarkDisabled) {
        set(unlinkedBenchmarkType(selected), get(globalBenchmarkType));
        set(unlinkedBenchmarkSubject(selected), get(globalBenchmarkSubject));
        set(unlinkedBenchmarkRelative(selected), get(globalBenchmarkRelative));
      }
      // Theoretically you could switch a block to unlinked while the global state is still loading.
      // E2E tests do this frequently because they set an investment on the global, and then within <100ms they click a block and unlink the block.
      // It is ok in these rare situations to leave the block with a default range.
      const range = get(waitForNone({ globalDateRange })).globalDateRange.valueMaybe()?.range;
      range && set(overriddenBlockDateRange(selected), range);
      set(overriddenBlockSubjects(selected), get(globalSubjects));
      set(unlinkedSubjectFees(selected), get(globalSubjectFees));
    }
  },
});

export const rightPanelSelectedBlock = selector<CustomizedBlock | undefined>({
  key: 'rightPanelSelectedBlock',
  get: ({ get }) => {
    const selected = get(selectedBlockIdState);
    return selected ? get(customizedBlock(selected)) : undefined;
  },
});
